import { useMutation } from "@tanstack/react-query";
import {
  type IBillData,
  type IOverviewJointBillService,
  type IRequestedSpBillData,
} from "@services/overview/types";
import {
  type IEditServiceProviderDueDayRequest,
  type IEditServiceProviderRequest,
} from "@services/serviceProviders/types";
import { queryClient } from "@/queryClient";
import {
  addRequestedServiceProvider,
  addServiceProvider,
  editServiceProvider,
  editServiceProviderDueDay,
  getRequestedSpBillFile,
  removeRequestedSp,
  removeServiceProvider,
  restoreServiceProvider,
  transferServiceProvider,
} from "./services";

export const useAddServiceProviderMutation = () =>
  useMutation({
    mutationFn: addServiceProvider,
    onSuccess: async (response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", variables.jointBillServiceId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServiceSummaries"],
      });
      if (response.planChanged) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              "jointBillServiceSettings",
              variables.jointBillServiceId,
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: ["selectedJointBillServiceProviderBillPlans"],
          }),
        ]);
      }
    },
  });

export const useEditServiceProviderMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
) =>
  useMutation({
    mutationFn: (data: IEditServiceProviderRequest) =>
      editServiceProvider(
        jointBillServiceId,
        jointBillServiceAgreementId,
        data,
      ),
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId],
      }),
  });

export const useEditServiceProviderDueDayMutation = () =>
  useMutation({
    mutationFn: (data: IEditServiceProviderDueDayRequest) =>
      editServiceProviderDueDay(data),
    onSuccess: async (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", variables.jointBillServiceId],
      }),
  });

export const useRemoveServiceProviderMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
) =>
  useMutation({
    mutationFn: () =>
      removeServiceProvider(jointBillServiceId, jointBillServiceAgreementId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicePeriodData", jointBillServiceId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSummaries"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["nonProfitProviders", jointBillServiceId],
        }),
      ]);
    },
  });

export const useTransferServiceProviderMutation = () =>
  useMutation({
    mutationFn: transferServiceProvider,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicePeriodData"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSummaries"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["nonProfitProviders"],
        }),
      ]);
    },
  });

export const useRestoreServiceProviderMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
) =>
  useMutation({
    mutationFn: () =>
      restoreServiceProvider(jointBillServiceId, jointBillServiceAgreementId),
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId],
      }),
  });

export const useAddRequestedServiceProviderMutation = () =>
  useMutation({
    mutationFn: addRequestedServiceProvider,
    onSuccess: (_, { jointBillServiceId }) =>
      queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId],
      }),
  });

export const useRemoveRequestedSpMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  requestedServiceProviderBillId: IRequestedSpBillData["requestedServiceProviderBillId"],
) =>
  useMutation({
    mutationFn: () => removeRequestedSp(requestedServiceProviderBillId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId],
      }),
  });

export const useGetRequestedSpBillFileMutation = () =>
  useMutation({
    mutationFn: getRequestedSpBillFile,
  });
